import React, { useEffect, useState } from "react";
import { Compass, Loader2, TrendingUp, Wind, Shield } from "lucide-react";
import { fetchLeapsSingle } from "@/lib/api";

const STATUS_STYLE = {
  green: { fg: "#00C805", bg: "rgba(0,200,5,0.12)", bd: "rgba(0,200,5,0.4)", label: "Sell Edge", verb: "Sell calls" },
  amber: { fg: "#FFD60A", bg: "rgba(255,214,10,0.12)", bd: "rgba(255,214,10,0.4)", label: "Wait", verb: "Wait" },
  red:   { fg: "#FF3B30", bg: "rgba(255,59,48,0.12)", bd: "rgba(255,59,48,0.4)", label: "Hold LEAPs", verb: "Hold" },
  unknown: { fg: "#7A7A7A", bg: "rgba(255,255,255,0.06)", bd: "rgba(255,255,255,0.15)", label: "No data", verb: "—" },
};

/**
 * IncomeCompassCard
 * Fetches /api/leaps/{symbol} and renders the green/amber/red gauge answering:
 * "Is now a good window to sell a covered call against your LEAPs?"
 * `onData` optional callback receives the full leaps payload so the parent
 * (AnalysisContent) can wire the adaptive-exit stop into SymbolChart.
 */
export const IncomeCompassCard = ({ symbol, onData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchLeapsSingle(symbol)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        onData?.(r);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Failed"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [symbol, onData]);

  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/10 rounded-xl p-5" data-testid="income-compass-loading">
        <div className="flex items-center gap-2 text-white/50 text-[12px]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading Income Compass…
        </div>
      </div>
    );
  }
  if (err || !data) {
    return null; // Silent fail — this card is a bonus, not required.
  }

  const ic = data.income_compass || {};
  const style = STATUS_STYLE[ic.status] || STATUS_STYLE.unknown;
  const tr = data.trend || {};
  const vp = data.vol_pressure || {};
  const mo = data.momentum || {};

  return (
    <div
      className="rounded-xl border p-5 bg-gradient-to-br from-black/40 to-transparent"
      style={{ borderColor: style.bd }}
      data-testid="income-compass-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4" style={{ color: style.fg }} />
          <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: style.fg }}>
            Income Compass
          </span>
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border"
          style={{ color: style.fg, background: style.bg, borderColor: style.bd }}
          data-testid="income-compass-status"
        >
          {style.label}
        </span>
      </div>

      <div className="text-[15px] font-medium text-white mb-1.5">
        {ic.status === "green" && `Sell a call against your ${symbol} LEAPs.`}
        {ic.status === "amber" && `Wait — mixed signals for ${symbol}.`}
        {ic.status === "red" && `Hold the LEAPs uncapped on ${symbol}.`}
        {ic.status === "unknown" && `Not enough data on ${symbol} yet.`}
      </div>
      {ic.detail && (
        <p className="text-[12px] text-white/65 leading-relaxed mb-3" data-testid="income-compass-detail">
          {ic.detail}
        </p>
      )}

      {/* Supporting-signal chips */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] pt-3 border-t border-white/[0.06]">
        <span className="inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-white/40" />
          <span className="text-white/40 uppercase tracking-[0.12em]">Trend</span>
          <span className="text-white/80">{tr.daily ?? "—"} / {tr.weekly ?? "—"}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Wind className="w-3 h-3 text-white/40" />
          <span className="text-white/40 uppercase tracking-[0.12em]">IV rank</span>
          <span className="text-white/80 num">{vp.iv_rank != null ? vp.iv_rank.toFixed(0) : "—"}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="text-white/40 uppercase tracking-[0.12em]">Momentum</span>
          <span className="text-white/80 font-semibold">{mo.grade || "—"}</span>
        </span>
        <span className="inline-flex items-center gap-1 ml-auto">
          <Shield className="w-3 h-3 text-white/40" />
          <span className="text-white/40 uppercase tracking-[0.12em]">Ripcord</span>
          <span className="text-white/80 num">
            {data.adaptive_exit?.stop_price != null ? `$${data.adaptive_exit.stop_price.toFixed(2)}` : "—"}
          </span>
        </span>
      </div>
    </div>
  );
};

export default IncomeCompassCard;
